import {
	getSelectedLocation,
	setSelectedLocation,
	updateLocationSelector,
} from "./location-state.js";

function safeJsonParse(value, fallback = null) {
	if (!value) {
		return fallback;
	}

	try {
		return JSON.parse(value);
	} catch (error) {
		console.error("Failed to parse JSON payload", error);
		return fallback;
	}
}

const moduleData = safeJsonParse(
	document.getElementById("wp-script-module-data-@assets/js/pre-header.js")
		?.innerHTML,
	{},
);
const siteUrl = moduleData.siteUrl;
const locationsPageUrl = moduleData.locationsPageUrl ?? `${siteUrl}/locations`;
const franchiseLocations = Array.isArray(moduleData.locations)
	? moduleData.locations
	: [];
function getLocationSlug(location) {
	return location.city.toLowerCase().replace(/'/g, "").replace(/ /g, "-");
}

function getUserLocation() {
	return new Promise((resolve, reject) => {
		navigator.geolocation.getCurrentPosition(
			(position) => {
				window.localStorage.setItem(
					"teresasPizzaUserLocation",
					JSON.stringify(position),
				);
				resolve(position);
			},
			(error) => {
				reject(error);
			},
		);
	});
}

async function fetchLocationDistances(userLocation) {
	const origin = `${userLocation.coords.latitude},${userLocation.coords.longitude}`;
	const destinations = franchiseLocations
		.map((location) => `${location.latitude},${location.longitude}`)
		.join("|");

	if (!destinations) {
		return [];
	}

	const url = `/wp-json/teresas-pizza/v1/distance-matrix?origins=${encodeURIComponent(origin)}&destinations=${encodeURIComponent(destinations)}`;
	const response = await fetch(url);
	const data = await response.json();

	if (!response.ok) {
		throw new Error("Failed to fetch distances");
	}

	if (!data.rows || !data.rows[0] || !data.rows[0].elements) {
		throw new Error("Invalid distance matrix response");
	}

	return data.rows[0].elements
		.map((element, index) => {
			const location = franchiseLocations[index];
			return {
				id: location.id,
				name: location.name,
				distance: element.distance ? element.distance.text : "N/A",
				duration: element.duration ? element.duration.text : "N/A",
				durationValue: element.duration
					? element.duration.value
					: Number.MAX_VALUE,
				location,
			};
		})
		.sort((a, b) => a.durationValue - b.durationValue);
}

const locationSelector = document.querySelector(".location-selector");
const locationSwitcher = document.querySelector(".location-switcher");

function normalizeUrlPath(value) {
	try {
		return new URL(value, window.location.origin).pathname.replace(/\/$/, "");
	} catch (error) {
		return "";
	}
}

function isLocationsPage() {
	return normalizeUrlPath(window.location.href) === normalizeUrlPath(locationsPageUrl);
}

function scrollToSiteContent() {
	const siteContent = document.querySelector(".site-content");
	if (siteContent) {
		siteContent.scrollIntoView({ behavior: "smooth", block: "start" });
		return;
	}

	window.scrollTo({ top: 0, behavior: "smooth" });
}

function generateMenuLink(location) {
	const menuLink = location
		? `${siteUrl}/${getLocationSlug(location)}/menu`
		: `${siteUrl}/menu`;
	const navItems = document.querySelectorAll(".main-navigation .menu-item");
	navItems.forEach((item) => {
		if (item.textContent.toLowerCase().includes("menu")) {
			const linkElement = item.querySelector("a");
			if (linkElement) {
				linkElement.href = menuLink;
			}
		}
	});
}

function renderSwitcherBaseContent() {
	if (!locationSwitcher) {
		return;
	}

	const selectedLocation = getSelectedLocation();
	locationSwitcher.innerHTML = `
        <div class="location-switcher-panel">
          <div class="location-switcher-header">
            <p class="location-switcher-eyebrow">Location</p>
            <h3>${selectedLocation ? selectedLocation.name : "Choose your Teresa's Pizza"}</h3>
            </div>
            <p class="location-switcher-copy">${selectedLocation ? `${selectedLocation.address}, ${selectedLocation.city}, ${selectedLocation.state}` : "Set a store for menu links, specials, and faster ordering."}</p>
            <div class="location-switcher-actions">
                <button class="location-switcher-button" data-action="use-location" type="button">Use My Location</button>
                <a class="location-switcher-link" href="${locationsPageUrl}">Browse All Locations</a>
            </div>
            ${selectedLocation ? `<div class="location-switcher-footer"><a class="location-switcher-link location-switcher-link-strong" href="${siteUrl}/${getLocationSlug(selectedLocation)}/menu">View ${selectedLocation.city} Menu</a></div>` : ""}
        </div>
    `;

	const useLocationButton = locationSwitcher.querySelector(
		'[data-action="use-location"]',
	);
	useLocationButton?.addEventListener("click", handleUseMyLocation);

	const browseLocationsLink = locationSwitcher.querySelector(
		`.location-switcher-link[href="${locationsPageUrl}"]`,
	);
	browseLocationsLink?.addEventListener("click", (event) => {
		if (!isLocationsPage()) {
			return;
		}

		event.preventDefault();
		closeSwitcher();
		scrollToSiteContent();
	});
}

function renderNearestLocations(distances) {
	if (!locationSwitcher) {
		return;
	}

	const nearestLocations = distances.slice(0, 3);
	const existingQuickPicks = locationSwitcher.querySelector(
		".location-switcher-quick-picks",
	);

	if (!nearestLocations.length) {
		existingQuickPicks?.remove();
		return;
	}

	const quickPicks = existingQuickPicks ?? document.createElement("div");
	quickPicks.className = "location-switcher-quick-picks";
	quickPicks.innerHTML = `
        <div class="location-switcher-results">
            <p class="location-switcher-results-label">Nearest stores</p>
            ${nearestLocations
							.map(
								({ location, distance, duration }) => `
                <button class="location-switcher-result" type="button" data-location-id="${location.id}">
                    <span>
                        <strong>${location.name}</strong>
                        <small>${location.city}, ${location.state}</small>
                    </span>
                    <span class="location-switcher-distance">${distance} · ${duration}</span>
                </button>
            `,
							)
							.join("")}
        </div>
    `;

	if (!existingQuickPicks) {
		const footer = locationSwitcher.querySelector(".location-switcher-footer");
		if (footer) {
			locationSwitcher.querySelector(".location-switcher-panel")?.insertBefore(
				quickPicks,
				footer,
			);
		} else {
			locationSwitcher.querySelector(".location-switcher-panel")?.appendChild(
				quickPicks,
			);
		}
	}

	quickPicks.querySelectorAll("[data-location-id]").forEach((button) => {
		button.addEventListener("click", () => {
			const locationId = button.getAttribute("data-location-id");
			const selected = nearestLocations.find(
				({ location }) => String(location.id) === String(locationId),
			);
			if (!selected) {
				return;
			}

			setSelectedLocation(selected.location);
			updateLocationSelector();
			generateMenuLink(selected.location);
			closeSwitcher();
		});
	});
}

async function handleUseMyLocation() {
	if (!locationSwitcher) {
		return;
	}

	const button = locationSwitcher.querySelector('[data-action="use-location"]');
	if (button) {
		button.disabled = true;
		button.textContent = "Finding Nearby Stores...";
	}

	try {
		const cachedLocation = safeJsonParse(
			window.localStorage.getItem("teresasPizzaUserLocation"),
		);
		const userLocation = cachedLocation ?? (await getUserLocation());
		const distances = await fetchLocationDistances(userLocation);
		renderNearestLocations(distances);
	} catch (error) {
		const existingQuickPicks = locationSwitcher.querySelector(
			".location-switcher-quick-picks",
		);
		const quickPicks = existingQuickPicks ?? document.createElement("div");
		quickPicks.className = "location-switcher-quick-picks";
		quickPicks.innerHTML =
			'<p class="location-switcher-error">We could not determine your location. Browse the full map instead.</p>';

		if (!existingQuickPicks) {
			const footer = locationSwitcher.querySelector(".location-switcher-footer");
			if (footer) {
				locationSwitcher.querySelector(".location-switcher-panel")?.insertBefore(
					quickPicks,
					footer,
				);
			} else {
				locationSwitcher.querySelector(".location-switcher-panel")?.appendChild(
					quickPicks,
				);
			}
		}
	} finally {
		if (button) {
			button.disabled = false;
			button.textContent = "Use My Location";
		}
	}
}

function openSwitcher() {
	if (!locationSelector || !locationSwitcher) {
		return;
	}

	renderSwitcherBaseContent();
	locationSwitcher.hidden = false;
	locationSelector.setAttribute("aria-expanded", "true");
}

function closeSwitcher() {
	if (!locationSelector || !locationSwitcher) {
		return;
	}

	locationSwitcher.hidden = true;
	locationSelector.setAttribute("aria-expanded", "false");
}

function toggleSwitcher() {
	if (!locationSwitcher) {
		return;
	}

	if (locationSwitcher.hidden) {
		openSwitcher();
	} else {
		closeSwitcher();
	}
}

document.addEventListener("DOMContentLoaded", () => {
	const selectedLocation = getSelectedLocation();
	updateLocationSelector();
	generateMenuLink(selectedLocation);

	const locationSlug = new URLSearchParams(window.location.search).get(
		"location",
	);
	if (locationSlug) {
		const location = franchiseLocations.find(
			(item) => getLocationSlug(item) === locationSlug.toLowerCase(),
		);
		if (location) {
			setSelectedLocation(location);
			updateLocationSelector();
			generateMenuLink(location);
		}
	}

	if (!locationSelector || !locationSwitcher) {
		return;
	}

	locationSelector.addEventListener("click", toggleSwitcher);

	document.addEventListener("click", (event) => {
		if (locationSwitcher.hidden) {
			return;
		}

		if (
			!locationSwitcher.contains(event.target) &&
			!locationSelector.contains(event.target)
		) {
			closeSwitcher();
		}
	});

	document.addEventListener("keydown", (event) => {
		if (event.key === "Escape") {
			closeSwitcher();
		}
	});
});
